import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import Swal from "sweetalert2";

import { APIMS } from "../../../../../config/apims";
import ToastError from "../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../NotificationToast/ToastSuccess";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";

import "bulma/css/bulma.css";
import "../../../../../index.css";

const pageStyles = `
  .event-header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .event-header-action {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .event-toolbar {
    // display: flex;
    // width: 100%;
    // justify-content: flex-end;
    margin-bottom: 14px;
  }

  .event-search {
    // width: min(100%, auto);
    position: relative;
  }

  

  .event-table {
    min-width: 560px;
  }

  .event-col-name {
    width: auto;
  }

  .event-name-text {
    display: block;
    color: #24314e;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    white-space: normal;
    overflow-wrap: break-word;
  }

  @media (max-width: 780px) {
    .event-header-top {
      align-items: stretch;
      flex-direction: column;
    }

    .event-header-action,
    .event-header-action .btn-create {
      width: 100%;
    }

    .event-header-action .btn-create {
      justify-content: center;
    }

    .event-search {
      width: 100%;
    }
  }
`;

export default function TipeAcara() {
  document.title = "Tipe Acara";

  const token = localStorage.getItem("token");
  const ascending = 0;

  const [allData, setAllData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [query, setQuery] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const GetResponseData = async () => {
    setLoading(true);

    try {
      const response = await APIMS.get(
        `/api/events?page=1&limit=1000&ascending=${ascending}&search=`,
        fetchParams
      );

      setAllData(response?.data?.data || []);
    } catch (error) {
      setAllData([]);

      ToastError.fire({
        icon: "error",
        title: error?.response?.data?.message || "Gagal memuat data",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GetResponseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      setKeyword(query.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(delay);
  }, [query]);

  const filteredData = useMemo(() => {
    const searchValue = keyword.toLowerCase();

    if (!searchValue) {
      return allData;
    }

    return allData.filter((item) =>
      String(item?.name_event || "")
        .toLowerCase()
        .includes(searchValue)
    );
  }, [allData, keyword]);

  const totalRows = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / limit));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredData.slice(start, start + limit);
  }, [filteredData, page, limit]);

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    const pages = [];

    if (totalPages <= maxButtons) {
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        pages.push(pageNumber);
      }

      return pages;
    }

    const left = Math.max(1, page - 1);
    const right = Math.min(totalPages, page + 1);

    pages.push(1);

    if (left > 2) {
      pages.push("left-dots");
    }

    for (let pageNumber = left; pageNumber <= right; pageNumber += 1) {
      if (pageNumber !== 1 && pageNumber !== totalPages) {
        pages.push(pageNumber);
      }
    }

    if (right < totalPages - 1) {
      pages.push("right-dots");
    }

    pages.push(totalPages);

    return pages;
  }, [page, totalPages]);

  const goToPage = (nextPage) => {
    const destination = Math.min(
      Math.max(1, Number(nextPage) || 1),
      totalPages
    );

    setPage(destination);
  };

  const deleteById = (id) => {
    Swal.fire({
      title: "Apakah kamu yakin?",
      text: "Data yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await APIMS.delete(
            `/api/events/${id}`,
            fetchParams
          );

          if (response?.data?.error) {
            throw new Error(response?.data?.message);
          }

          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error?.response?.data?.message ||
              error?.message ||
              "Gagal menghapus data"
          );

          return false;
        }
      },
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      ToastSuccess.fire({
        icon: "success",
        title: "Data berhasil dihapus",
      });

      GetResponseData();
    });
  };

  const openUpdateModal = (row) => {
    setSelectedId(row?.id);
    setSelectedName(row?.name_event || "");
    setModalUpdate(true);
  };

  return (
    <div className="body module-page">
      <style>{pageStyles}</style>

      {modalAdd && (
        <ModalAdd
          GetResponseData={GetResponseData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          GetResponseData={GetResponseData}
          id={selectedId}
          nameUpdate={selectedName}
          show={modalUpdate}
          onHide={() => setModalUpdate(false)}
        />
      )}

      <div className="page-header-card">
        <div className="event-header-top">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faCalendarCheck} />
            </span>
            <h4 className="page-title-text">Tipe Acara</h4>
          </div>

          <div className="event-header-action">
            <button
              type="button"
              onClick={() => setModalAdd(true)}
              className="btn btn-create module-header-button"
            >
              <FontAwesomeIcon icon={faPlus} />
              Tambah Tipe Acara
            </button>
          </div>
        </div>

        <nav
          aria-label="breadcrumb"
          className="custom-breadcrumb-nav mt-4"
        >
          <ol className="custom-breadcrumb">
            <li className="custom-breadcrumb-item">
              <Link to="/kalender-akademik">Kalender Akademik</Link>
            </li>
            <li className="custom-breadcrumb-separator">/</li>
            <li
              className="custom-breadcrumb-item active"
              aria-current="page"
            >
              Tipe Acara
            </li>
          </ol>
        </nav>
      </div>

      <div className="body-content module-content">
        <div className="event-toolbar">
          <div className="event-search">
            <div className="module-search-wrap">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="module-search-icon"
              />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="module-search-input"
                placeholder="Cari tipe acara..."
                aria-label="Cari tipe acara"
              />
            </div>
          </div>
        </div>

        <div className="module-table-card">
          {loading ? (
            <div className="module-loading">
              <PulseLoader color="#3f5bea" size={11} />
            </div>
          ) : (
            <>
              <div className="module-table-scroll">
                <table className="module-table event-table">
                  <thead>
                    <tr>
                      <th className="module-col-number">No</th>
                      <th className="event-col-name">Nama Tipe Acara</th>
                      <th className="module-col-action">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="module-empty-state">
                          <div className="module-empty-state-content">
                            Data tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((row, index) => (
                        <tr key={row?.id || index}>
                          <td className="module-col-number">
                            <span className="module-number-badge">
                              {(page - 1) * limit + index + 1}
                            </span>
                          </td>
                          <td>
                            <span className="event-name-text">
                              {row?.name_event || "-"}
                            </span>
                          </td>
                          <td className="module-col-action">
                            <div className="module-action-wrap">
                              <button
                                type="button"
                                className="module-action-button module-action-edit"
                                onClick={() => openUpdateModal(row)}
                                title="Edit tipe acara"
                                aria-label="Edit tipe acara"
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>

                              <button
                                type="button"
                                className="module-action-button module-action-delete"
                                onClick={() => deleteById(row?.id)}
                                title="Hapus tipe acara"
                                aria-label="Hapus tipe acara"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="module-pagination">
                <div className="module-pagination-left">
                  <div className="module-page-info">
                    Total <b>{totalRows}</b> data · Halaman <b>{page}</b> dari{" "}
                    <b>{totalPages}</b>
                  </div>

                  <select
                    className="module-page-size"
                    value={limit}
                    onChange={(event) => {
                      setLimit(Number(event.target.value));
                      setPage(1);
                    }}
                    aria-label="Jumlah data per halaman"
                  >
                    <option value={10}>10 data per halaman</option>
                    <option value={25}>25 data per halaman</option>
                    <option value={50}>50 data per halaman</option>
                    <option value={100}>100 data per halaman</option>
                  </select>
                </div>

                <div className="module-pagination-right">
                  <div className="module-page-numbers">
                    <button
                      type="button"
                      className="module-page-nav"
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      aria-label="Halaman sebelumnya"
                    >
                      &lt;
                    </button>

                    {pageNumbers.map((item) =>
                      typeof item === "string" ? (
                        <span key={item} className="module-page-dots">
                          ...
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={item}
                          className={`module-page-number ${
                            item === page ? "active" : ""
                          }`}
                          onClick={() => goToPage(item)}
                          aria-current={item === page ? "page" : undefined}
                        >
                          {item}
                        </button>
                      )
                    )}

                    <button
                      type="button"
                      className="module-page-nav"
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      aria-label="Halaman berikutnya"
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
